/**
 * Agreements.js
 * Gestão de Convênios (parceiros com desconto para clientes avulsos)
 * CI Sistemas de Informática Ltda. — FastALPR Parking — Fase 2
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { hojeLocal } from '../utils/dataLocal'; // Onda 138: data local, não UTC
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiTag, FiSearch } from 'react-icons/fi';
import '../styles/Agreements.css';

const AgreementModal = ({ agreement, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: '', description: '', discount_type: 'percentual', discount_value: '',
    validation_code: '', max_uses_day: '', max_parking_hours: '', valid_from: hojeLocal(),
    valid_until: '', active: true,
    ...(agreement || {})
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (agreement) {
        await api.put(`/api/agreements/${agreement.id}`, form);
        toast.success('Convênio atualizado!');
      } else {
        await api.post('/api/agreements', form);
        toast.success('Convênio criado!');
      }
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar convênio');
    } finally { setSaving(false); }
  };

  const discountLabel = () => {
    if (form.discount_type === 'percentual') return `${form.discount_value || 0}% de desconto`;
    if (form.discount_type === 'valor_fixo') return `R$ ${form.discount_value || 0} de desconto`;
    if (form.discount_type === 'horas_gratis') return `${form.discount_value || 0} hora(s) gratuita(s)`;
    if (form.discount_type === 'isencao_total') return 'Isenção total (gratuito)';
    return '';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{agreement ? 'Editar Convênio' : 'Novo Convênio'}</h3>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Nome do Convênio *</label>
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ex: Convênio Hospital São Lucas, Desconto Funcionários..." />
          </div>
          <div className="form-group">
            <label>Descrição</label>
            <textarea rows={2} value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} placeholder="Descrição do convênio e regras de uso..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Tipo de Desconto *</label>
              <select value={form.discount_type} onChange={e => setForm({...form, discount_type: e.target.value})}>
                <option value="percentual">Percentual (%)</option>
                <option value="valor_fixo">Valor Fixo (R$)</option>
                <option value="horas_gratis">Horas Gratuitas</option>
                <option value="isencao_total">Isenção Total</option>
              </select>
            </div>
            {form.discount_type !== 'isencao_total' && (
              <div className="form-group">
                <label>
                  {form.discount_type === 'percentual' ? 'Percentual (%)' :
                   form.discount_type === 'valor_fixo' ? 'Valor (R$)' : 'Horas Gratuitas'} *
                </label>
                <input required type="number" step="0.01" min="0"
                  value={form.discount_value}
                  onChange={e => setForm({...form, discount_value: e.target.value})}
                  placeholder={form.discount_type === 'percentual' ? '0 a 100' : '0,00'} />
              </div>
            )}
          </div>
          {form.discount_value && (
            <div className="discount-preview">
              <span>💡 {discountLabel()}</span>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Código de validação no PDV</label>
              <input value={form.validation_code || ''} onChange={e => setForm({...form, validation_code: e.target.value.toUpperCase()})} placeholder="Ex: HOSPITAL10" />
            </div>
            <div className="form-group">
              <label>Limite de usos por dia</label>
              <input type="number" min="1" value={form.max_uses_day || ''} onChange={e => setForm({...form, max_uses_day: e.target.value})} placeholder="Vazio = sem limite" />
            </div>
          </div>
          <div className="form-group">
            <label>Tempo Máximo de Permanência (horas, opcional)</label>
            <input type="number" min="1" value={form.max_parking_hours || ''} onChange={e => setForm({...form, max_parking_hours: e.target.value})} placeholder="Ex: 4 (vazio = sem limite)" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Válido a partir de *</label>
              <input required type="date" value={form.valid_from} onChange={e => setForm({...form, valid_from: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Válido até (deixe vazio = sem vencimento)</label>
              <input type="date" value={form.valid_until || ''} onChange={e => setForm({...form, valid_until: e.target.value})} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Agreements = () => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/agreements', { params: search ? { search } : {} });
      setAgreements(res.data.data || []);
    } catch (err) { toast.error('Erro ao carregar convênios'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Desativar o convênio "${item.name}"?`)) return;
    try {
      await api.delete(`/api/agreements/${item.id}`);
      toast.success('Convênio desativado');
      loadData();
    } catch (err) { toast.error('Erro ao desativar convênio'); }
  };

  const getDiscountLabel = (a) => {
    if (a.discount_type === 'percentual') return `${a.discount_value}% desconto`;
    if (a.discount_type === 'valor_fixo') return `R$ ${parseFloat(a.discount_value).toFixed(2)} desconto`;
    if (a.discount_type === 'horas_gratis') return `${a.discount_value}h gratuitas`;
    if (a.discount_type === 'isencao_total') return 'Isenção total';
    return a.discount_type;
  };

  const discountColors = {
    percentual: '#3b82f6', valor_fixo: '#10b981',
    horas_gratis: '#f59e0b', isencao_total: '#8b5cf6'
  };

  return (
    <div className="agreements-page">
      <div className="page-header">
        <div>
          <h1>🤝 Convênios</h1>
          <p className="page-subtitle">Parcerias com desconto para clientes avulsos</p>
        </div>
        <button className="btn-primary" onClick={() => { setEditItem(null); setShowModal(true); }}>
          <FiPlus /> Novo Convênio
        </button>
      </div>

      <div className="agreements-filters">
        <div className="search-box">
          <FiSearch />
          <input placeholder="Buscar por nome do convênio..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Carregando convênios...</div>
      ) : agreements.length === 0 ? (
        <div className="empty-state">
          <FiTag size={48} />
          <h3>Nenhum convênio cadastrado</h3>
          <p>Cadastre parcerias para oferecer descontos a clientes avulsos</p>
        </div>
      ) : (
        <div className="agreements-grid">
          {agreements.map(a => (
            <div key={a.id} className={`agreement-card ${!a.active ? 'inactive' : ''}`}>
              <div className="agreement-card-header">
                <div className="agreement-icon">🤝</div>
                <div className="agreement-info">
                  <div className="agreement-name">{a.name}</div>
                  {a.description && <div className="agreement-desc">{a.description}</div>}
                </div>
                <div className="agreement-actions">
                  <button className="btn-icon btn-edit" onClick={() => { setEditItem(a); setShowModal(true); }}><FiEdit2 /></button>
                  <button className="btn-icon btn-delete" onClick={() => handleDelete(a)}><FiTrash2 /></button>
                </div>
              </div>
              <div className="agreement-card-body">
                <span className="discount-badge" style={{background: (discountColors[a.discount_type] || '#6c63ff') + '15', color: discountColors[a.discount_type] || '#6c63ff'}}>
                  🏷️ {getDiscountLabel(a)}
                </span>
                {a.max_parking_hours && <span className="meta-badge">⏱️ Máx. {a.max_parking_hours}h</span>}
                {a.validation_code && <span className="meta-badge">🔐 {a.validation_code}</span>}
                {a.max_uses_day && <span className="meta-badge">🎫 Até {a.max_uses_day}/dia</span>}
                <span className="meta-badge">📅 {a.valid_from ? new Date(a.valid_from).toLocaleDateString('pt-BR') : 'Início imediato'}{a.valid_until ? ` até ${new Date(a.valid_until).toLocaleDateString('pt-BR')}` : ' (sem vencimento)'}</span>
                <span className="meta-badge">✅ {a.total_uses || 0} uso(s) · ↩ {a.reversed_uses || 0} reversão(ões)</span>
              </div>
              {!a.active && <div className="inactive-overlay">INATIVO</div>}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <AgreementModal agreement={editItem} onClose={() => setShowModal(false)} onSaved={loadData} />
      )}
    </div>
  );
};

export default Agreements;
